import React, { useId } from "react";
import { Switch } from "../../primitives/Switch";
import { FormControlWrapper } from "../FormControlWrapper";
import { FormControlOutline } from "../FormControlWrapper/FormControlOutline";
import { FormControlLabel } from "../FormControlWrapper/FormControlLabel";
import { splitFormControlWrapperProps } from "../FormControlWrapper/helpers";
import { cn } from "@gettactic/helpers";
import {
  ToggleColorVariant,
  ToggleInputProps,
  ToggleInputPropsWithFormControl,
  TogglePosition
} from "./ToggleInput.types";

const colorVariantStyles: Record<
  ToggleColorVariant,
  { label: string; description: string }
> = {
  default: {
    label: "",
    description: "text-muted-foreground"
  },
  destructive: {
    label: "text-red-900",
    description: "text-red-800"
  }
};

const ToggleInputInput = React.forwardRef<
  React.ComponentRef<typeof Switch>,
  ToggleInputProps
>((props, ref) => {
  const {
    checked,
    onCheckedChange,
    disableOutline,
    togglePosition,
    colorVariant = "default",
    verticalCenter,
    ...rest
  } = props;

  return (
    <Switch
      {...rest}
      checked={checked}
      onCheckedChange={onCheckedChange}
      color={colorVariant}
      ref={ref}
    />
  );
});

ToggleInputInput.displayName = "ToggleInputInput";

export const ToggleInput = React.forwardRef<
  React.ComponentRef<typeof Switch>,
  ToggleInputPropsWithFormControl
>((props, ref) => {
  const {
    togglePosition = "bottom",
    colorVariant = "default",
    verticalCenter = false,
    ...restProps
  } = props;
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(restProps);
  const {
    label,
    description,
    info,
    isRequired,
    isOptional,
    ...restWrapperProps
  } = wrapperProps;
  const { checked, onCheckedChange, disabled } = inputProps;
  const variantStyles = colorVariantStyles[colorVariant];

  const handleLabelClick = () => {
    if (!disabled && onCheckedChange && checked !== undefined) {
      onCheckedChange(!checked);
    }
  };

  // For left/right positions, we need a custom layout
  if (togglePosition === "left" || togglePosition === "right") {
    return (
      <div className="w-full">
        <div
          className={cn(
            "flex justify-between gap-4",
            verticalCenter ? "items-center" : "items-start"
          )}
        >
          {togglePosition === "left" && (
            <div
              className={cn(
                "flex shrink-0 items-center",
                !verticalCenter && "pt-0.5"
              )}
            >
              <ToggleInputInput
                ref={ref}
                {...inputProps}
                togglePosition={togglePosition}
                colorVariant={colorVariant}
              />
            </div>
          )}
          <div
            className={cn(
              "flex min-w-0 flex-1 flex-col",
              !disabled && onCheckedChange && "cursor-pointer"
            )}
            onClick={(e) => {
              // Don't trigger if clicking on info tooltip or its trigger
              const target = e.target as HTMLElement;
              if (
                target.closest(
                  '[role="tooltip"], [data-radix-tooltip-content], [data-radix-tooltip-trigger], button'
                )
              ) {
                return;
              }
              handleLabelClick();
            }}
            role="button"
            tabIndex={disabled ? undefined : 0}
            onKeyDown={(e) => {
              if (!disabled && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                handleLabelClick();
              }
            }}
          >
            {label && (
              <FormControlLabel
                isRequired={isRequired}
                isOptional={isOptional}
                info={info}
                className={variantStyles.label}
              >
                {label}
              </FormControlLabel>
            )}
            {description && (
              <p className={cn("text-md mt-1", variantStyles.description)}>
                {description}
              </p>
            )}
          </div>
          {togglePosition === "right" && (
            <div
              className={cn(
                "flex w-12 shrink-0 items-center justify-end",
                !verticalCenter && "pt-0.5"
              )}
            >
              <ToggleInputInput
                ref={ref}
                {...inputProps}
                togglePosition={togglePosition}
                colorVariant={colorVariant}
              />
            </div>
          )}
        </div>
        {inputProps.disableOutline !== true && <FormControlOutline />}
      </div>
    );
  }

  // Default bottom position - use FormControlWrapper
  // We need to make the label and description clickable
  const toggleId = useId();

  const handleLabelAreaClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking on info tooltip or its trigger
    const target = e.target as HTMLElement;
    if (
      target.closest(
        '[role="tooltip"], [data-radix-tooltip-content], [data-radix-tooltip-trigger], button'
      )
    ) {
      return;
    }
    handleLabelClick();
  };

  const handleLabelAreaKeyDown = (e: React.KeyboardEvent) => {
    if (!disabled && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleLabelClick();
    }
  };

  return (
    <FormControlWrapper
      variant={"unstyled"}
      {...restWrapperProps}
      label={
        label ? (
          <div
            className={cn(
              "w-full",
              !disabled && onCheckedChange && "cursor-pointer",
              variantStyles.label
            )}
            onClick={handleLabelAreaClick}
            role="button"
            tabIndex={disabled ? undefined : 0}
            onKeyDown={handleLabelAreaKeyDown}
          >
            {label}
          </div>
        ) : undefined
      }
      description={
        description ? (
          <div
            className={cn(
              !disabled && onCheckedChange && "cursor-pointer",
              variantStyles.description
            )}
            onClick={handleLabelAreaClick}
            role="button"
            tabIndex={disabled ? undefined : 0}
            onKeyDown={handleLabelAreaKeyDown}
          >
            {description}
          </div>
        ) : undefined
      }
      info={info}
      isRequired={isRequired}
      isOptional={isOptional}
    >
      <ToggleInputInput
        ref={ref}
        {...inputProps}
        togglePosition={togglePosition}
        colorVariant={colorVariant}
        id={toggleId}
      />
      {inputProps.disableOutline !== true && <FormControlOutline />}
    </FormControlWrapper>
  );
});

ToggleInput.displayName = "ToggleInput";
